/**
 * Bundle 161 - Soft in-app nudge banner for users whose signup
 * is incomplete. Non-blocking. Complements Bundle 160's blocking
 * modal for NEW signups by giving existing incomplete users a
 * gentle reminder they can dismiss for 24h.
 *
 * Visibility rule: user must have `signup_completion_required==true`
 * AND (nudge_last_dismissed_at is null OR older than 24h AND
 *      not already dismissed in this browser within 24h).
 */
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import api from '@/utils/api';

const NUDGE_LOCAL_KEY = 'b161_nudge_dismissed_at';
const RESURFACE_HOURS = 24;

const _isFresh = (iso) => {
  if (!iso) return false;
  try {
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return false;
    const ageHours = (Date.now() - t) / 3.6e6;
    return ageHours < RESURFACE_HOURS;
  } catch (_e) { return false; }
};

const IncompleteProfileBanner = ({ user, onCompleteClicked }) => {
  const [localDismissed, setLocalDismissed] = useState(() => {
    try { return _isFresh(localStorage.getItem(NUDGE_LOCAL_KEY)); }
    catch (_e) { return false; }
  });

  const shouldShow = useMemo(() => {
    if (!user) return false;
    if (!user.signup_completion_required) return false;
    if (localDismissed) return false;
    if (_isFresh(user.nudge_last_dismissed_at)) return false;
    return true;
  }, [user, localDismissed]);

  if (!shouldShow) return null;

  const dismiss = async () => {
    const nowIso = new Date().toISOString();
    try { localStorage.setItem(NUDGE_LOCAL_KEY, nowIso); }
    catch (_e) { /* localStorage unavailable */ }
    setLocalDismissed(true);
    try {
      await api.post('/auth/dismiss-completion-nudge');
    } catch (_e) {
      /* server-side stamp is best-effort; local dismiss is
         authoritative for the 24h window. */
    }
  };

  return (
    <div
      className="w-full bg-amber-50 border-b border-amber-200 py-2 px-4 flex items-center justify-between gap-3 flex-wrap"
      data-testid="incomplete-profile-banner"
      role="status"
    >
      <div className="text-sm text-amber-900 flex-1 min-w-0">
        <span className="font-semibold">Almost done.</span>
        {' '}Add your date of birth and last name to keep RSVP-ing.
        Some events require it for age verification.
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={onCompleteClicked}
          className="bg-primary-900 text-white hover:bg-primary-800"
          data-testid="incomplete-profile-banner-cta"
        >Complete now</Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={dismiss}
          className="text-amber-800 hover:bg-amber-100"
          data-testid="incomplete-profile-banner-dismiss"
          aria-label="Dismiss reminder"
        >
          <X className="h-4 w-4 mr-1" /> Later
        </Button>
      </div>
    </div>
  );
};

export default IncompleteProfileBanner;
